import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    createBrowserRouter,
    RouterProvider,
} from 'react-router-dom';
import {
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';

import Cart from '../pages/Cart';
import Dashboard from '../pages/Dashboard';
import ProtectedRoutes from './ProtectRoutes';
import PublicRoutes from './PublicRoutes';
import RequirePermission from './RequirePermission';

import styles from './styles.module.css';

import Layout from '@/components/Layout';
import AuthContext, {
    type AuthState,
    type TokenPair,
    type UserData,
} from '@/contexts/auth';
import {
    baseRequest,
    setForbiddenHandler,
} from '@/lib/base';
import {
    EMPTY_RBAC,
    type RbacSnapshot,
    snapshotFromWhoami,
} from '@/lib/rbac';
import {
    clearTokens,
    getTokens,
    setTokens,
} from '@/lib/utils';
import ChancoPrivacyPolicy from '@/pages/ChancoPrivacyPolicy';
import ChancoTermsAndConditions from '@/pages/ChancoTermsAndConditions';
import ContactUs from '@/pages/ContactUs';
import DeleteAccount from '@/pages/DeleteAccount';
import ForgotPassword from '@/pages/ForgotPassword';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import NewPassword from '@/pages/NewPassword';
import NotFound from '@/pages/NotFound';
import Onboarding from '@/pages/Onboarding';
import OTPVerification from '@/pages/OTPVerification';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Signup from '@/pages/Signup';

interface TokenResponse {
    data: {
        tokens: {
            access: string;
            refresh: string;
        };
        user: {
            id: number;
            uuid: string;
            email: string;
            owner_full_name: string;
            username?: string;
            business_name?: string;
        };
    };
    status: number;
    statusText: string;
}

interface ApiError {
    message: string;
}

// Helper function to check if access token is valid (not expired)
const isTokenValid = (token: string): boolean => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiryTime = payload.exp * 1000;
        const currentTime = Date.now();
        // Consider token valid if it has more than 1 minute left
        return expiryTime > currentTime + 60000;
    } catch (error) {
        return false;
    }
};

const refreshTokens = async (): Promise<TokenResponse> => {
    const { refreshToken } = getTokens();
    if (!refreshToken) {
        throw new Error('No refresh token available');
    }

    const response = await baseRequest({
        url: '/system/auth/refresh',
        method: 'POST',
        body: {
            refresh: refreshToken,
        },
    });

    return response as TokenResponse;
};

const fetchWhoami = async (): Promise<RbacSnapshot> => {
    const response = await baseRequest({
        url: '/system/auth/whoami/',
        method: 'GET',
    });
    if (!response.ok) {
        return EMPTY_RBAC;
    }
    const payload = response.data?.data ?? response.data;
    return snapshotFromWhoami(payload?.team_membership);
};

const router = createBrowserRouter([
    {
        path: '/',
        element: <Landing />,
    },
    {
        path: '/onboarding',
        element: <Onboarding />,
    },
    {
        path: '/tipa/privacy-policy',
        element: <PrivacyPolicy />,
    },
    {
        path: '/tipa/delete-account',
        element: <DeleteAccount />,
    },
    {
        path: '/tipa/contact-us',
        element: <ContactUs />,
    },
    {
        path: '/chanco/privacy-policy',
        element: <ChancoPrivacyPolicy />,
    },
    {
        path: '/chanco/terms-and-conditions',
        element: <ChancoTermsAndConditions />,
    },
    {
        element: <PublicRoutes />,
        children: [
            {
                path: '/login',
                element: <Login />,
            },
            {
                path: '/signup',
                element: <Signup />,
            },
            {
                path: '/forgot-password',
                element: <ForgotPassword />,
            },
            {
                path: '/verify-otp',
                element: <OTPVerification />,
            },
            {
                path: '/new-password',
                element: <NewPassword />,
            },
        ],
    },
    {
        element: <Layout />,
        children: [
            {
                element: <ProtectedRoutes />,
                children: [
                    {
                        element: <RequirePermission />,
                        children: [
                            {
                                path: '/dashboard',
                                element: <Dashboard />,
                            },
                            {
                                path: '/related-parties',
                                lazy: () => import(
                                    '../pages/RelatedParties'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/my-parties',
                                lazy: () => import(
                                    '../pages/RelatedParties/MyParties'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/manual',
                                lazy: () => import(
                                    '../pages/RelatedParties/ManualParties'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/add',
                                lazy: () => import(
                                    '../pages/RelatedParties/AddParty'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/requests',
                                lazy: () => import(
                                    '../pages/RelatedParties/Requests'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/favorites',
                                lazy: () => import(
                                    '../pages/RelatedParties/Favorites'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/blocked',
                                lazy: () => import(
                                    '../pages/RelatedParties/Blocked'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/map',
                                lazy: () => import(
                                    '../pages/RelatedParties/MapView'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/party/:userId',
                                lazy: () => import(
                                    '../pages/RelatedParties/PartyProfile'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/party/:userId/products',
                                lazy: () => import(
                                    '../pages/RelatedParties/PartyProducts'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/party/:userId/products/:slug',
                                lazy: () => import(
                                    '../pages/RelatedParties/PartyProductDetail'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/party/:userId/transactions',
                                lazy: () => import(
                                    '../pages/RelatedParties/PartyTransactionHistory'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/related-parties/party/:userId/reviews',
                                lazy: () => import(
                                    '../pages/RelatedParties/PartyReviews'
                                ).then((m) => ({
                                    Component: m.default,
                                })),
                            },
                            {
                                path: '/inventory',
                                lazy: () => import('../pages/Inventory').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/inventory/groups',
                                lazy: () => import('../pages/Inventory/Groups').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/inventory/categories',
                                lazy: () => import('../pages/Inventory/Categories').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/inventory/units',
                                lazy: () => import('../pages/Inventory/Units').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/inventory/products',
                                lazy: () => import('../pages/Inventory/Products').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/inventory/products/create',
                                lazy: () => import('../pages/Inventory/ProductForm').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/inventory/products/catalog',
                                lazy: () => import('../pages/Inventory/CatalogImport').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/inventory/order-items/import',
                                lazy: () => import('../pages/Inventory/OrderItemImport').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/inventory/products/:slug/detail',
                                lazy: () => import('../pages/Inventory/ProductDetail').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/inventory/products/:slug/edit',
                                lazy: () => import('../pages/Inventory/ProductForm').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/respond-orders',
                                lazy: () => import('../pages/MyOrders/RespondOrders').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/respond-orders/:uuid',
                                lazy: () => import('../pages/MyOrders/OrderDetail').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/quotations/request',
                                lazy: () => import('../pages/Quotations/RequestQuotation').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/quotations/respond',
                                lazy: () => import('../pages/Quotations/RespondQuotation').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/announcements',
                                lazy: () => import('../pages/Announcements').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/announcements/create',
                                lazy: () => import('../pages/Announcements/CreateAnnouncement').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/announcements/:slug/edit',
                                lazy: () => import('../pages/Announcements/EditAnnouncement').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/reports',
                                lazy: () => import('../pages/Reports').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/reports/inventory-valuation',
                                lazy: () => import('../pages/Reports/InventoryValuation').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/reports/item-wise-balance',
                                lazy: () => import('../pages/Reports/ItemWiseBalance').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/reports/parties',
                                lazy: () => import('../pages/Reports/PartyPicker').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/reports/parties/:partyId/transactions',
                                lazy: () => import('../pages/Reports/PartyTransactions').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/reports/products',
                                lazy: () => import('../pages/Reports/ProductPicker').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/reports/products/:slug/transactions',
                                lazy: () => import('../pages/Reports/ProductTransactions').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/reports/performance',
                                lazy: () => import('../pages/Reports/Performance').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/reports/team-activity',
                                lazy: () => import('../pages/Reports/TeamActivity').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/settings',
                                lazy: () => import('../pages/Settings').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/contact',
                                lazy: () => import('../pages/Contact').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/genres',
                                lazy: () => import('../pages/Genres').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/tags',
                                lazy: () => import('../pages/Tags').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/blogs',
                                lazy: () => import('../pages/Blogs').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/blogs/create',
                                lazy: () => import('../pages/Blogs/BlogsUpdate').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/blogs/:id/edit',
                                lazy: () => import('../pages/Blogs/BlogsUpdate').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/resources',
                                lazy: () => import('../pages/Resources').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/resources/create',
                                lazy: () => import('../pages/Resources/ResourcesUpdate').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/resources/:id/edit',
                                lazy: () => import('../pages/Resources/ResourcesUpdate').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/links',
                                lazy: () => import('../pages/Links').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/books',
                                lazy: () => import('../pages/Books').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/books/create',
                                lazy: () => import('../pages/Books/BooksUpdate').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/books/:id/edit',
                                lazy: () => import('../pages/Books/BooksUpdate').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/reviews',
                                lazy: () => import('../pages/Reviews').then((module) => ({ Component: module.default })),
                            },
                            {
                                path: '/orders',
                                lazy: () => import('../pages/MyOrders/OrdersHub').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/orders/create',
                                lazy: () => import('../pages/MyOrders/CreateOrder').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/orders/incoming',
                                lazy: () => import('../pages/MyOrders/RespondOrders').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/orders/outgoing',
                                lazy: () => import('../pages/MyOrders').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/orders/saved',
                                lazy: () => import('../pages/SavedOrders').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/orders/saved/:uuid',
                                lazy: () => import('../pages/SavedOrders/SavedOrderDetail').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/orders/:uuid',
                                lazy: () => import('../pages/MyOrders/OrderDetail').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/cart',
                                element: <Cart />,
                            },
                            {
                                path: '/chat',
                                lazy: () => import('../pages/Chat').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/return',
                                lazy: () => import('../pages/Return').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/return/incoming',
                                lazy: () => import('../pages/Return/Incoming').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/return/outgoing',
                                lazy: () => import('../pages/Return/Outgoing').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/return/order-return',
                                lazy: () => import('../pages/Return/OrderReturn').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/return/order-return/:partyId',
                                lazy: () => import('../pages/Return/OrderReturn/Create').then((m) => ({ Component: m.default })),
                            },
                            {
                                path: '/return/:uuid',
                                lazy: () => import('../pages/Return/Detail').then((m) => ({ Component: m.default })),
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: '*',
        element: <NotFound />,
    },
]);

function App() {
    const [auth, setAuth] = useState<AuthState | undefined>(undefined);
    const [rbac, setRbac] = useState<RbacSnapshot | null>(null);
    const [ready, setReady] = useState(false);
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const queryClient = useQueryClient();

    const refreshRbac = useCallback(async () => {
        const snapshot = await queryClient.fetchQuery({
            queryKey: ['whoami'],
            queryFn: fetchWhoami,
            staleTime: 0,
        });
        setRbac(snapshot);
    }, [queryClient]);

    useEffect(() => {
        setForbiddenHandler(() => {
            queryClient.invalidateQueries({ queryKey: ['whoami'] });
            refreshRbac().catch(() => undefined);
        });
        return () => setForbiddenHandler(null);
    }, [queryClient, refreshRbac]);

    // Resync the RBAC snapshot when the user returns to the tab. This catches
    // the case where the team owner edited the user's department permissions
    // in another session (or another browser) — without this the cached
    // snapshot would stay stale until the next 403 or login.
    useEffect(() => {
        if (!auth) return undefined;
        const handler = () => {
            if (document.visibilityState === 'visible') {
                refreshRbac().catch(() => undefined);
            }
        };
        window.addEventListener('focus', handler);
        document.addEventListener('visibilitychange', handler);
        return () => {
            window.removeEventListener('focus', handler);
            document.removeEventListener('visibilitychange', handler);
        };
    }, [auth, refreshRbac]);

    const logout = useCallback(() => {
        setAuth(undefined);
        setRbac(null);
        clearTokens();
        localStorage.removeItem('business-name');

        // Clear all cached queries so next login starts fresh
        queryClient.clear();

        // Clear refresh timeout
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }
    }, [queryClient]);

    // Function to calculate token expiry and set refresh timeout
    const startTokenRefreshPolling = useCallback((accessToken: string) => {
        try {
            // Decode JWT to get expiry time
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            const expiryTime = payload.exp * 1000; // Convert to milliseconds
            const currentTime = Date.now();

            // Refresh 5 minutes before expiry (300,000ms)
            const refreshTime = expiryTime - currentTime - 300000;

            // Clear existing timeout
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }

            // Set timeout for token refresh
            if (refreshTime > 0) {
                refreshTimeoutRef.current = setTimeout(async () => {
                    try {
                        const response = await refreshTokens();
                        if (response.data?.tokens) {
                            setTokens(response.data.tokens.access, response.data.tokens.refresh);

                            // Continue the refresh cycle
                            startTokenRefreshPolling(response.data.tokens.access);
                        } else {
                            logout();
                        }
                    } catch (error) {
                        console.error('Token refresh failed:', error);
                        logout();
                    }
                }, refreshTime);
            } else {
                // Token already expired, try immediate refresh
                refreshTokens()
                    .then((response) => {
                        if (response.data?.tokens) {
                            setTokens(response.data.tokens.access, response.data.tokens.refresh);
                            startTokenRefreshPolling(response.data.tokens.access);
                        } else {
                            logout();
                        }
                    })
                    .catch(() => {
                        logout();
                    });
            }
        } catch (error) {
            console.error('Error parsing access token:', error);
            logout();
        }
    }, [logout]);

    const login = useCallback((tokens: TokenPair, user: UserData, rememberMe = true) => {
        // Store both tokens using utility function
        setTokens(tokens.access, tokens.refresh, rememberMe);

        // Store business name and username in localStorage for persistence
        localStorage.setItem('business-name', user.business_name || '');
        localStorage.setItem('username', user.username || '');

        // Set auth state
        setAuth({
            id: user.id,
            name: user.owner_full_name,
            businessName: user.business_name || '',
            username: user.username || '',
        });

        // Start token refresh polling
        startTokenRefreshPolling(tokens.access);

        // Fetch RBAC snapshot in the background so the UI gates correctly.
        refreshRbac().catch(() => undefined);
    }, [startTokenRefreshPolling, refreshRbac]);

    const authContextValue = useMemo(
        () => ({
            state: auth,
            setState: setAuth,
            rbac,
            setRbac,
            refreshRbac,
            login,
            logout,
        }),
        [login, logout, auth, rbac, refreshRbac],
    );

    const {
        isError,
        isLoading,
        error,
        data: tokenData,
    } = useQuery<TokenResponse, ApiError>({
        queryKey: ['token-refresh'],
        queryFn: () => refreshTokens(),
        enabled: !ready && (() => {
            const { accessToken, refreshToken } = getTokens();
            // Only refresh if we have a refresh token but no valid access token
            return !!refreshToken && (!accessToken || !isTokenValid(accessToken));
        })(),
        retry: false, // Don't retry on failure
    });

    useEffect(() => {
        const { accessToken, refreshToken } = getTokens();

        // If no refresh token, user is not logged in
        if (!refreshToken) {
            logout();
            setReady(true);
            return;
        }

        // If we have a valid access token, set auth immediately without refresh
        if (accessToken && isTokenValid(accessToken)) {
            try {
                const payload = JSON.parse(atob(accessToken.split('.')[1]));
                // Set auth state from stored token + localStorage
                setAuth({
                    id: payload.user_id,
                    name: localStorage.getItem('business-name') || 'User',
                    businessName: localStorage.getItem('business-name') || '',
                    username: localStorage.getItem('username') || '',
                });

                // Start token refresh polling with existing token
                startTokenRefreshPolling(accessToken);
                refreshRbac().catch(() => undefined);
                setReady(true);
                return;
            } catch (parseError) {
                console.error('Error parsing stored access token:', parseError);
                // If we can't parse the token, fall through to refresh logic
            }
        }

        // If we have token data from the refresh query
        if (tokenData) {
            if (tokenData.status === 401 || tokenData.status === 403) {
                // Refresh token is invalid
                logout();
                setReady(true);
            } else if (tokenData.data?.tokens && tokenData.data?.user) {
                // Successful token refresh
                const { tokens, user } = tokenData.data;

                setTokens(tokens.access, tokens.refresh);

                localStorage.setItem('business-name', user.business_name || '');
                localStorage.setItem('username', user.username || '');
                setAuth({
                    id: user.id,
                    name: user.owner_full_name,
                    businessName: user.business_name || '',
                    username: user.username || '',
                });

                // Start token refresh polling
                startTokenRefreshPolling(tokens.access);
                refreshRbac().catch(() => undefined);
                setReady(true);
            } else {
                // Unexpected response format
                logout();
                setReady(true);
            }
        }

        // If we have a refresh token but no valid access token and no query data yet,
        // the query will handle the refresh automatically
    }, [logout, tokenData, startTokenRefreshPolling, refreshRbac]);

    // Handle query error
    useEffect(() => {
        if (isError) {
            logout();
            setReady(true);
        }
    }, [isError, logout]);

    // Cleanup timeout on unmount
    useEffect(() => () => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }
    }, []);

    if (!ready) {
        return (
            <div className={styles.fallbackElement}>
                <img src="/chanco-logo-only.png" alt="Chanco" className="h-8 w-8" />
                Chanco loading...
            </div>
        );
    }

    if (isError && error) {
        return (
            <div>
                Error:
                {error.message}
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={styles.fallbackElement}>
                <img src="/chanco-logo-only.png" alt="Chanco" className="h-8 w-8" />
                Chanco loading...
            </div>
        );
    }

    return (
        <AuthContext.Provider value={authContextValue}>
            <RouterProvider
                router={router}
                fallbackElement={(
                    <div className={styles.fallbackElement}>
                        <img src="/chanco-logo-only.png" alt="Chanco" className="h-8 w-8" />
                        Chanco loading...
                    </div>
                )}
            />
        </AuthContext.Provider>
    );
}

export default App;
